import React, { useMemo, useState } from "react";
import { useAppContext } from "../../../contexts/AppContext";
import { findAirlineLogoFromAdmin } from "../../../utils/airlineLogoFromAdmin";
import AirlineIdentity from "../components/AirlineIdentity";
import FlightTimeline from "../components/FlightTimeline";
import BaggageSummary from "../components/BaggageSummary";
import FareBadge from "../components/FareBadge";
import PriceBlock from "../components/PriceBlock";
import "./FlightOfferCard.css";

export default function FlightOfferCard({
  offer,
  selected = false,
  /** @param {string} offerId */
  onSelect,
}) {
  const [fareOpen, setFareOpen] = useState(false);
  const { airlines } = useAppContext();
  const airlineCode = offer?.airline?.code || offer?.airline?.logoText || "";
  const logoSrc = useMemo(
    () => findAirlineLogoFromAdmin(airlines, airlineCode),
    [airlines, airlineCode]
  );
  if (!offer) return null;

  const sharedChips = (offer.baggage?.chips || []).filter(
    (chip) => !isLegIncludedChip(chip?.label)
  );

  const className = [
    "fe2-offer",
    selected ? "fe2-offer--selected" : "",
    offer.flags?.cheapest ? "fe2-offer--cheapest" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={className}>
      <div className="fe2-offer__grid">
        <div className="fe2-offer__airline">
          <div className="fe2-offer__identity">
            {offer.flags?.cheapest ? (
              <span className="fe2-offer__cheapest">הכי זול</span>
            ) : null}
            <AirlineIdentity
              airline={offer.airline}
              secondaryAirline={offer.secondaryAirline}
              size="lg"
              logoSrc={logoSrc}
            />
          </div>
          <div className="fe2-offer__fare-badges">
            {(offer.fareBadges || []).map((b) => (
              <FareBadge key={b}>{b}</FareBadge>
            ))}
          </div>
        </div>

        <div className="fe2-offer__itinerary">
          <FlightTimeline segment={offer.outbound} />
          <FlightTimeline segment={offer.inbound} />
          {sharedChips.length ? (
            <div className="fe2-offer__benefits">
              <BaggageSummary chips={sharedChips} />
            </div>
          ) : null}
          {offer.fareFamilies?.length ? (
            <button
              type="button"
              className="fe2-offer__fares-toggle"
              onClick={() => setFareOpen((v) => !v)}
            >
              {fareOpen ? "הסתר משפחות מחיר" : "הצג משפחות מחיר (preview)"}
            </button>
          ) : null}
        </div>

        <div className="fe2-offer__commercial">
          <PriceBlock
            price={offer.price}
            onSelect={() => onSelect?.(offer.id)}
          />
        </div>
      </div>

      {fareOpen && offer.fareFamilies?.length ? (
        <div className="fe2-offer__fares">
          {offer.fareFamilies.map((ff) => (
            <div key={ff.id} className="fe2-offer__fare-row">
              <strong>{ff.name}</strong>
              <span className="fe2-ltr">{ff.priceLabel}</span>
              <span>{ff.trolley}</span>
              <span>{ff.checked}</span>
              <span>{ff.changes}</span>
              <span>{ff.refund}</span>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

/** Included trolley/checked labels render on each leg, not as one shared footer. */
function isLegIncludedChip(label) {
  const s = String(label || "").trim();
  return /^(?:טרולי(?: בלבד)?|\d+\s*טרולי|\d+\s*מזוודות|מזוודה)$/u.test(s);
}
